import React from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { FiInfo, FiSmartphone, FiMonitor, FiAlertTriangle, FiLink, FiCalendar, FiImage } from "react-icons/fi";
import { MARKETS, DEFAULT_COUNTRY } from "../../config/markets.js";
import { C, SLOT_META, describePlacement, describeLinkState } from "./adCenterMeta.js";

// ── Placement column + details popover ───────────────────────────────────────
// One cell that answers, for THIS row: where will it show, with which artwork,
// and is anything wrong. The ⓘ opens a popover with the full picture (slot
// description, per-surface artwork, link destination, market, schedule).

const SURFACE_ICON = {
  android: <FiSmartphone size={10} />,
  ios: <FiSmartphone size={10} />,
  mweb: <FiSmartphone size={10} />,
  desktop: <FiMonitor size={10} />,
};

const chipStyle = (tone) => ({
  display: "inline-flex", alignItems: "center", gap: 3,
  fontSize: 10.5, fontWeight: 600, lineHeight: 1.4,
  padding: "2px 7px", borderRadius: 999, whiteSpace: "nowrap",
  background: tone === "warn" ? C.orangeSoft : C.tealSoft,
  color: tone === "warn" ? C.orange : C.teal,
  border: `1px solid ${tone === "warn" ? "#f3d9b4" : "#bfeee5"}`,
});

const marketDate = (value, country) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: MARKETS[country]?.timezone || "Africa/Kampala",
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    }).format(d);
  } catch (e) {
    return d.toLocaleString();
  }
};

const Row = ({ icon, title, children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "16px 1fr", gap: 8, marginBottom: 10 }}>
    <span style={{ color: C.textLight, paddingTop: 2 }}>{icon}</span>
    <div>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: C.textLight, marginBottom: 3 }}>{title}</div>
      <div style={{ fontSize: 12.5, color: C.textMid, lineHeight: 1.5 }}>{children}</div>
    </div>
  </div>
);

// Must forward the ref and spread the overlay props: OverlayTrigger clones
// this element with Popper's `ref`, `style`, `className`, `placement`,
// `arrowProps`, `popper` and `hasDoneInitialMeasure`. Swallow any of them and
// the panel renders at the document origin with no positioning at all.
export const AdDetailsPopover = React.forwardRef(({ ad, className, ...overlayProps }, ref) => {
  const placement = describePlacement(ad);
  const link = describeLinkState(ad);
  const country = ad.country || DEFAULT_COUNTRY;
  const slotMeta = SLOT_META[ad.slot];
  const artworkFor = (key) => {
    const s = placement.surfaces.find((x) => x.key === key);
    if (!s) return null;
    if (s.artwork === "phone") return "phone artwork";
    if (s.artwork === "desktop") return "desktop artwork";
    return "phone artwork, cropped to a strip";
  };
  const groups = [
    { label: "Android app", key: "android" },
    { label: "iOS app", key: "ios" },
    { label: "Mobile web", key: "mweb" },
    { label: "Desktop web", key: "desktop" },
  ];

  return (
    // No `style` prop here: react-bootstrap merges the overlay's own style
    // AFTER Popper's, so an inline style would wipe the computed position and
    // the panel would sit at the document origin. Width comes from the
    // scoped stylesheet below instead.
    <Popover
      ref={ref}
      {...overlayProps}
      id={`ad-details-${ad._id}`}
      className={`ad-details-popover${className ? ` ${className}` : ""}`}
    >
      <Popover.Header as="div" style={{ fontSize: 13, fontWeight: 700, color: C.text, background: C.bg }}>
        {ad.title}
        <div style={{ fontSize: 11, fontWeight: 500, color: C.textLight, marginTop: 2 }}>{placement.slotLabel}</div>
      </Popover.Header>
      <Popover.Body>
        {slotMeta && (
          <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.5, marginBottom: 12 }}>{slotMeta.renders}</div>
        )}

        <Row icon={<FiMonitor size={13} />} title="Shows on">
          {placement.surfaces.length === 0 ? (
            <span style={{ color: C.orange, fontWeight: 600 }}>Nowhere</span>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 10px" }}>
              {groups.map((g) => {
                const uses = artworkFor(g.key);
                return (
                  <React.Fragment key={g.key}>
                    <span style={{ color: uses ? C.text : C.textLight, fontWeight: uses ? 600 : 400 }}>{g.label}</span>
                    <span style={{ color: uses ? (uses.includes("cropped") ? C.orange : C.textMid) : C.textLight }}>
                      {uses || "not shown"}
                    </span>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </Row>

        <Row icon={<FiImage size={13} />} title="Artwork">
          {placement.artworks.length === 0
            ? <span style={{ color: C.orange }}>None</span>
            : placement.artworks.map((a) => (
              <div key={a.key}>{a.label}: {a.size}</div>
            ))}
        </Row>

        <Row icon={<FiLink size={13} />} title="A tap opens">
          <span style={{ color: link.tone === "warn" ? C.orange : C.textMid }}>{link.detail}</span>
          {ad.buttonText && <div style={{ color: C.textLight }}>Website button text: "{ad.buttonText}"</div>}
        </Row>

        <Row icon={<FiCalendar size={13} />} title="Market and schedule">
          <div>{MARKETS[country]?.name || country} customers</div>
          <div>
            {ad.startsAt ? `From ${marketDate(ad.startsAt, country)}` : "No start date"}
            {" · "}
            {ad.endsAt ? `until ${marketDate(ad.endsAt, country)}` : "no end date"}
          </div>
          <div style={{ color: C.textLight }}>Position {ad.order ?? 0} in the carousel{ad.isActive ? "" : " · paused"}</div>
        </Row>

        {placement.warnings.map((w) => (
          <div key={w} style={{ display: "flex", gap: 6, alignItems: "flex-start", background: C.orangeSoft, border: "1px solid #f3d9b4", borderRadius: 6, padding: "7px 10px", marginBottom: 8, fontSize: 12, color: C.textMid }}>
            <FiAlertTriangle size={12} style={{ color: C.orange, flexShrink: 0, marginTop: 2 }} /> {w}
          </div>
        ))}
      </Popover.Body>
    </Popover>
  );
});
AdDetailsPopover.displayName = "AdDetailsPopover";

// Page-scoped styles for the details panel (dashboard convention: a small
// <style> next to the component rather than a global sheet). Rendered once
// by AdvertManager.
export const AdCenterStyles = () => (
  <style>{`
    .ad-details-popover { max-width: 380px; font-family: inherit; }
    .ad-details-popover .popover-body { padding: 14px 16px 6px; }
  `}</style>
);

export const PlacementCell = ({ ad }) => {
  const placement = describePlacement(ad);
  // Amber when anything about this row needs a person's attention: a surface
  // problem, or a link that opens nothing.
  const hasWarnings = placement.warnings.length > 0 || describeLinkState(ad).tone === "warn";

  return (
    <div style={{ minWidth: 210 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{placement.slotLabel}</span>
        {/* Rendered into <body>: the admin layout has transformed and
            overflow-clipped ancestors (sidebar, table scrollport), so an
            in-place overlay positions against the wrong box and gets cut. */}
        <OverlayTrigger
          trigger="click"
          rootClose
          placement="auto"
          container={() => document.body}
          overlay={<AdDetailsPopover ad={ad} />}
        >
          <button
            type="button"
            aria-label={`Details for ${ad.title}`}
            title="Where this banner shows, with which artwork, and what a tap opens"
            style={{
              background: hasWarnings ? C.orangeSoft : C.blueSoft, border: "none", borderRadius: 999,
              width: 18, height: 18, display: "inline-flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: hasWarnings ? C.orange : C.blue, padding: 0, flexShrink: 0,
            }}
          >
            {hasWarnings ? <FiAlertTriangle size={10} /> : <FiInfo size={11} />}
          </button>
        </OverlayTrigger>
      </div>

      {/* Where it shows: one chip per surface, coloured by whether the
          artwork drawn there is the right one. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 5 }}>
        {placement.surfaces.length === 0 ? (
          <span style={chipStyle("warn")}><FiAlertTriangle size={10} /> Shows nowhere</span>
        ) : (
          placement.surfaces.map((s) => (
            <span key={s.key} style={chipStyle(s.artwork === "phone-cropped" ? "warn" : "ok")} title={s.artwork === "phone-cropped" ? "Draws the phone artwork cropped to a strip" : undefined}>
              {SURFACE_ICON[s.key]} {s.label}
            </span>
          ))
        )}
      </div>

      {/* Artwork actually on the row, with sizes. */}
      <div style={{ fontSize: 10.5, color: C.textLight, marginTop: 4 }}>
        {placement.artworks.length === 0
          ? <span style={{ color: C.orange, fontWeight: 600 }}>No artwork</span>
          : placement.artworks.map((a, i) => (
            <span key={a.key}>{i > 0 ? " · " : ""}{a.label} {a.size}</span>
          ))}
        {placement.notes.map((n) => (
          <div key={n} style={{ color: C.textLight }}>{n}</div>
        ))}
        {placement.warnings.map((w) => (
          <div key={w} style={{ color: C.orange, fontWeight: 600 }}>{w}</div>
        ))}
      </div>
    </div>
  );
};

export default PlacementCell;
